import React, { useState } from 'react'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import OrderPhaseLabel from '../OrderPhaseLabel/OrderPhaseLabel'
import AppointmentIndicator from '../AppointmentIndicator/AppointmentIndicator'
import { Order, OrderLine, OrderState } from '../../model'
import { formatCurrencyValue, formatDate } from '../../helpers/utils'
import { createMessageThread } from '../../services/api/messages'
import StartThreadButton from '../StartThreadButton/StartThreadButton'
import { NavLink, useNavigate } from 'react-router-dom'
import { RemarksIndicator } from '../RemarksIndicator/RemarksIndicator'
import OrderStateLabel from '../OrderStateLabel/OrderStateLabel'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'

type OrderAction = 'startThread' | 'cancel' | 'viewDetails'

interface OrderItemProps {
    data: Order
    showPhase?: boolean
    showState?: boolean
    actionButtons?: OrderAction[]
}
const OrderItem: React.FunctionComponent<OrderItemProps> = ({
    data,
    showPhase = false,
    showState = false,
    actionButtons
}: OrderItemProps) => {
    // const [open, setOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()

    //For reference
    const currentOrder: Order = data

    const isPackage: boolean | undefined = currentOrder.products && currentOrder.products.length > 1
    // const hasProductOptions: boolean | undefined = currentOrder.products?.some((p) => p.options && p.options.length > 0)

    const createMessage = async (orderId: number) => {
        setLoading(true)
        const params: any = {
            order: currentOrder.id
        }
        if (orderId !== currentOrder.id) {
            params['subOrder'] = orderId
        }
        try {
            const messageId = await createMessageThread(params)
            navigate(`order/${currentOrder.id}/messages?selectedThread=${messageId}`)
        } catch (e) {
            console.log(e)
        }

        setLoading(false)
    }

    const handleStartThread = ({ value }: { value: number }) => {
        createMessage(value)
    }

    let threadOptions: { label: string | JSX.Element; value: number | undefined }[] = [
        {
            label: 'Contact administratie',
            value: currentOrder.id
        }
    ]

    const listOfProducts =
        currentOrder.products && currentOrder.products?.filter((p) => p.isEligibleForMessaging === true)
    const subItems = listOfProducts
        ? listOfProducts.map((product: OrderLine) => {
              return {
                  label: (
                      <>
                          Contact over <strong>{product.name}</strong>
                      </>
                  ),
                  value: product.id
              }
          })
        : []

    threadOptions = [...subItems, ...threadOptions]

    // const handleExpand = (e: React.MouseEvent) => {
    //     e.preventDefault()
    //     e.stopPropagation()
    //     setOpen(!open)
    // }

    return (
        <NavLink
            to={`/dashboard/orders/order/${currentOrder.id}`}
            state={{ order: currentOrder }}
            className='border-btm hover:bg-gray-50 hover:cursor-pointer first:rounded-t-xl'
        >
            <div className='flex relative flex-col w-full px-3 lg:px-4 text-[--tb-text] py-2 first:pt-4 '>
                <div className='grid items-center flex-grow grid-cols-12 gap-y-2 gap-x-3 lg:gap-x-4'>
                    {/*
                    Address stays always the same
                    Regardless of amount of products
                */}
                    <div className='col-span-full md:col-span-3 lg:col-span-3 flex items-center space-x-3'>
                        <div className='flex items-center space-x-3  w-full min-w-0'>
                            {/* 
                            // Disabled for now and can be deprecated if we don't need it
                            <div className='flex items-center justify-center text-sm w-5'>
                                {(isPackage || hasProductOptions) && (
                                    <button type='button' className='p-4' onClick={handleExpand}>
                                        <DynamicFaIcon iconName='chevron-down' rotation={open ? 180 : undefined} />
                                    </button>
                                )}
                            </div> */}

                            <IconWithBackground iconName='house' />
                            <div className='flex flex-col'>
                                <span className='font-semibold text-sm truncate text-wrap'>
                                    {currentOrder.address?.street} {currentOrder.address?.houseNumber}
                                    {currentOrder.address?.extension && ` (${currentOrder.address?.extension})`}
                                </span>
                                <span className='text-xs font-medium text-[--tb-textLight]'>
                                    {currentOrder.address?.zipcode} {currentOrder.address?.city}
                                </span>
                                {currentOrder.surface && (
                                    <span className='text-xs text-[--tb-primary]'>{currentOrder.surface} m2</span>
                                )}
                            </div>
                        </div>
                    </div>
                    {/*
                    //Product type,
                    //Singular = Displays first products information as icon
                    //Multiple = Displays "Pakket" with specific icon
                */}

                    <div className='col-span-9 pl-2 md:col-span-4 flex space-x-3 items-center lg:col-span-4'>
                        {/* @Todo: display dynamic icon here */}
                        <IconWithBackground size='small' iconName={isPackage ? 'box' : 'calendar'} />

                        <div className='flex flex-col justify-center w-full min-w-0'>
                            <span className='font-medium text-sm truncate text-wrap'>{data.title}</span>
                            <span className='text-xs'>
                                <strong className='mr-2 font-semibold'>Besteld:</strong>
                                <span className='text-[--tb-textLight]'>
                                    {currentOrder.orderDate && formatDate(currentOrder.orderDate)}
                                </span>
                            </span>
                        </div>
                    </div>

                    {/* Price */}
                    <div className='col-span-3 md:col-span-2 lg:col-span-1 flex flex-col justify-start text-xs text-right md:text-left'>
                        <span className='text-[--tb-textLight]'>Totaal</span>
                        {!!currentOrder.totalPrice ? (
                            <span className='font-semibold'>{formatCurrencyValue(currentOrder.totalPrice || 0)}</span>
                        ) : (
                            '-'
                        )}
                    </div>
                    {showState && currentOrder.state === OrderState.Requested && (
                        <div className='col-span-6 pl-2 lg:pl-0 lg:col-span-1'>
                            <OrderStateLabel state={currentOrder.state} />
                        </div>
                    )}

                    {/*  Order status - depending on current state  */}
                    {!(showState && currentOrder.state === OrderState.Requested) && showPhase && (
                        <div className='col-span-6 pl-2 lg:pl-0 lg:col-span-1'>
                            <OrderPhaseLabel
                                // phase={phase}
                                phase={currentOrder.phase}
                            />
                        </div>
                    )}

                    {/* Appointments && comments */}
                    {/* {!(showState && currentOrder.state === OrderState.Requested) && ( */}
                    <div className='col-span-6 md:col-span-2 lg:col-span-1 pl-2 flex justify-start space-x-2 items-center'>
                        {currentOrder.remarks && (
                            <RemarksIndicator remarks={currentOrder.remarks} orderId={currentOrder.id} />
                        )}
                        <AppointmentIndicator
                            // appointments={appointments}
                            appointments={currentOrder.appointments || undefined}
                            orderId={currentOrder.id}
                        />
                    </div>
                    {/* )} */}
                    {actionButtons && (
                        <div
                            className='col-span-12 md:col-span-full lg:col-span-2 flex items-center self-center justify-end'
                            onClick={(e) => e.preventDefault()}
                        >
                            {actionButtons.indexOf('cancel') > -1 && (
                                <button type='button' className='primaryBtn'>
                                    <DynamicFaIcon iconName='undo' />
                                </button>
                            )}
                            {actionButtons.indexOf('startThread') > -1 && (
                                <StartThreadButton
                                    disabled={loading}
                                    options={threadOptions}
                                    onChange={handleStartThread}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* HIDDEN PRODUCT LIST */}
                {/* 
                // Disabled for now and can be deprecated if we don't need it
                {open && (
                    <div className='flex w-full flex-col mt-4 border-top pt-2'>
                        {currentOrder.products?.map((product: OrderLine) => {
                            return (
                                <div key={product.id} className='pl-5'>
                                    <ProductItem
                                        size='small'
                                        product={product as Product}
                                        showOptions
                                        key={product.id}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )} */}
            </div>
        </NavLink>
    )
}

export default OrderItem
